import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, RefreshCw, FileUp, Info } from "lucide-react";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ApiError } from "@/api/client";
import { useLicense } from "@/api/queries/license";
import { useActivateLicense, useRetryLicense, useImportLicense } from "@/api/mutations/license";
import type { LicenseSnapshot, LicenseState } from "@/types";

const INPUT_CLASS =
  "flex h-10 w-full rounded-md border border-input bg-base px-3 py-2 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

const STATE_VARIANT: Record<LicenseState, string> = {
  LICENSED: "online",
  GRACE: "busy",
  DEGRADED: "destructive",
  LOCKED: "revoked",
  DISABLED: "disabled",
};
const STATE_LABEL: Record<LicenseState, string> = {
  LICENSED: "Lizenziert",
  GRACE: "Kulanz (Grace)",
  DEGRADED: "Eingeschränkt (Degraded)",
  LOCKED: "Gesperrt (Locked)",
  DISABLED: "Nicht aktiviert",
};

function fmt(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "—" : new Date(t).toLocaleString();
}
function errMsg(e: unknown): string {
  if (e instanceof ApiError) {
    const b = e.body as { code?: string; error?: string } | undefined;
    return b?.code ?? b?.error ?? e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-[13px]">
      <span className="text-text3">{label}</span>
      <span className="text-right font-medium text-text">{children}</span>
    </div>
  );
}

export function LicenseSection() {
  const { data, isLoading } = useLicense();
  const activate = useActivateLicense();
  const retry = useRetryLicense();
  const importLease = useImportLicense();

  const [activationKey, setActivationKey] = useState("");
  const [leaseJson, setLeaseJson] = useState("");

  if (isLoading || !data) {
    return (
      <SectionCard title="Lizenz" icon={<ShieldCheck size={16} />}>
        <div className="text-[13px] text-text3">Lädt…</div>
      </SectionCard>
    );
  }

  // Subsystem nicht konfiguriert (kein LICENSE_ENFORCEMENT) → reiner Hinweis, keine Aktionen.
  if (!data.enabled) {
    return (
      <SectionCard title="Lizenz" icon={<ShieldCheck size={16} />}>
        <div className="flex items-start gap-2.5 text-[13px] text-text2">
          <Info size={16} className="mt-0.5 shrink-0 text-text3" />
          <div>
            <p className="font-medium text-text">Lizenz-Enforcement ist nicht aktiviert.</p>
            <p className="mt-1 text-text3">
              Setze <code className="rounded bg-surface2 px-1">LICENSE_ENFORCEMENT=true</code> +{" "}
              <code className="rounded bg-surface2 px-1">LICENSE_PORTAL_URL</code> +{" "}
              <code className="rounded bg-surface2 px-1">LICENSE_VENDOR_KEYS</code> in der Master-Umgebung,
              um Aktivierung + Enforcement zu aktivieren.
            </p>
          </div>
        </div>
      </SectionCard>
    );
  }

  const s = data as Required<Pick<LicenseSnapshot, "state">> & LicenseSnapshot;
  const busy = activate.isPending || retry.isPending || importLease.isPending;

  function onActivate() {
    const key = activationKey.trim();
    if (!key) return;
    activate.mutate(key, {
      onSuccess: (r) => {
        setActivationKey("");
        toast.success(`Aktiviert — Status: ${STATE_LABEL[r.state]}`);
      },
      onError: (e) => toast.error(`Aktivierung fehlgeschlagen: ${errMsg(e)}`),
    });
  }
  function onRetry() {
    retry.mutate(undefined, {
      onSuccess: (r) => toast.success(`Aktualisiert — Status: ${STATE_LABEL[r.state]}`),
      onError: (e) => toast.error(`Refresh fehlgeschlagen: ${errMsg(e)}`),
    });
  }
  function onImport() {
    let parsed: unknown;
    try {
      parsed = JSON.parse(leaseJson);
    } catch {
      toast.error("Ungültiges JSON — erwartet { claims: {…}, signature: \"…\" }");
      return;
    }
    importLease.mutate(parsed, {
      onSuccess: (r) => {
        setLeaseJson("");
        toast.success(`Lease importiert — Status: ${STATE_LABEL[r.state]}`);
      },
      onError: (e) => toast.error(`Import fehlgeschlagen: ${errMsg(e)}`),
    });
  }

  return (
    <>
      <SectionCard
        title="Lizenz-Status"
        icon={<ShieldCheck size={16} />}
        action={<Badge variant={STATE_VARIANT[s.state] as never}>{STATE_LABEL[s.state]}</Badge>}
      >
        <div className="divide-y divide-border">
          <Row label="Kunde">{s.customer ?? "—"}</Row>
          <Row label="Edition">{s.edition ?? "—"}</Row>
          <Row label="Deployment-ID">
            <span className="font-mono text-[12px]">{s.deploymentId ?? "—"}</span>
          </Row>
          <Row label="Features">
            {s.features && s.features.length > 0 ? (
              <span className="flex flex-wrap justify-end gap-1">
                {s.features.map((f) => (
                  <Badge key={f} variant="outline">
                    {f}
                  </Badge>
                ))}
              </span>
            ) : (
              "—"
            )}
          </Row>
          <Row label="Limits">
            {s.limits
              ? `Agents ${s.limits.agents} · Users ${s.limits.users} · Nodes ${s.limits.nodes} · Channels ${s.limits.channels}`
              : "—"}
          </Row>
          <Row label="Lease-Sequenz">{s.leaseSeq ?? "—"}</Row>
          <Row label="Lease läuft ab">{fmt(s.leaseExpiresAt)}</Row>
          <Row label="Kulanz-Ende (Grace)">{fmt(s.graceEndsAt)}</Row>
          <Row label="Lizenz läuft ab">{fmt(s.licenseExpiresAt)}</Row>
          <Row label="Trial">{s.trial ? "ja" : "nein"}</Row>
          <Row label="Widerrufen">{s.revoked ? "ja" : "nein"}</Row>
          <Row label="Letzter Refresh">{fmt(s.lastRefreshAt)}</Row>
          {s.lastRefreshError && (
            <Row label="Letzter Fehler">
              <span className="text-danger">{s.lastRefreshError}</span>
            </Row>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Aktionen" icon={<KeyRound size={16} />}>
        <div className="space-y-5">
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-text">Aktivierungsschlüssel</label>
            <div className="flex gap-2">
              <input
                className={INPUT_CLASS}
                type="text"
                placeholder="amlk_…"
                value={activationKey}
                disabled={busy}
                onChange={(e) => setActivationKey(e.target.value)}
                data-testid="license-activation-key"
              />
              <Button onClick={onActivate} disabled={busy || !activationKey.trim()} data-testid="license-activate-btn">
                <KeyRound size={15} /> Aktivieren
              </Button>
            </div>
            <p className="mt-1 text-[12px] text-text3">
              Einmaliger Schlüssel aus dem Lizenz-Portal — bindet dieses Deployment und holt das erste Lease.
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-text">Sofort-Refresh</label>
            <Button variant="outline" onClick={onRetry} disabled={busy} data-testid="license-retry-btn">
              <RefreshCw size={15} /> Jetzt gegen Portal aktualisieren
            </Button>
            <p className="mt-1 text-[12px] text-text3">
              Erneuert das Lease sofort (statt auf den periodischen Loop zu warten).
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-text">Offline-Lease importieren</label>
            <textarea
              className={`${INPUT_CLASS} h-28 font-mono text-[12px]`}
              placeholder={'{ "claims": { … }, "signature": "…" }'}
              value={leaseJson}
              disabled={busy}
              onChange={(e) => setLeaseJson(e.target.value)}
              data-testid="license-import-json"
            />
            <div className="mt-2">
              <Button variant="outline" onClick={onImport} disabled={busy || !leaseJson.trim()} data-testid="license-import-btn">
                <FileUp size={15} /> Lease importieren
              </Button>
            </div>
            <p className="mt-1 text-[12px] text-text3">
              Für Air-Gap/Offline: ein vom Portal signiertes Lease als JSON. Wird verifiziert + gegen Replay geprüft.
            </p>
          </div>
        </div>
      </SectionCard>
    </>
  );
}
